import { useEffect, useMemo, useRef, useState } from "react";
import { clearWebFrames } from "./webFrameHost";
import { createActor } from "xstate";
import { useSelector } from "@xstate/react";
import type { SignageTimeline } from "@shared/types/signage";
import type {
  ChannelLogoPlacement,
  SignagePlaybackReport,
} from "@shared/types/foyer";
import {
  DEFAULT_CHANNEL_LOGO_OFFSET_X,
  DEFAULT_CHANNEL_LOGO_OFFSET_Y,
  DEFAULT_CHANNEL_LOGO_WIDTH,
} from "@shared/types/foyer";
import { playheadAt } from "@shared/signage";
import { reportSignagePlayback } from "../lib/signagePlayback";
import {
  isAbrItem,
  isManifestUrl,
  pickPlayable,
  pickPlayableIndex,
  DEAD_RETRY_MS,
  type SignageSource,
} from "./SignageSurface";
import ChannelLogoOverlay from "./ChannelLogoOverlay";
import { avplayAvailable } from "./avplay";
import {
  resolveItemPlayback,
  type PlaybackCaller,
  type ResolvedItemPlayback,
} from "./playbackLicence";
import { findSignageItemById, toDrmConfig } from "./playerSixDrm";
import {
  initSignageCache,
  queueSignageAssets,
  evictStale,
  getLocalUri,
} from "./localAssetCache";
import { FoyerScheduleClient } from "./foyerScheduleClient";
import { WebLogClient } from "./webLogClient";
import WebDeviceController from "./webDeviceController";
import {
  autoplayerMachine,
  type Snapshot,
} from "../signage-engine/machine/autoplayerMachine";
import {
  selectCurrentAsset,
  selectCurrentAssetIndex,
  selectCurrentContent,
  selectIsPlaybackCircuitBreakerOpen,
  selectIsSignagePlaying,
  selectNextAsset,
  selectPlaybackType,
} from "../signage-engine/machine/selectors";
import { ScheduleType } from "../signage-engine/types/Schedule";
import { setClockSkewMs } from "../signage-engine/machine/clockSkew";
import DualLayerPlayback from "./DualLayerPlayback";
import NativeFallbackLayer from "./NativeFallbackLayer";
import AvPlayFallbackLayer from "./AvPlayFallbackLayer";
import SignageDiagnostics, { type PollStatus } from "./SignageDiagnostics";

/**
 * One composite selector rather than several separate `useSelector` calls,
 * so the telemetry report is built from a SINGLE consistent snapshot -
 * several independent selectors can each observe a different tick's
 * snapshot if the actor updates between renders, which would report a
 * `playlistName` from one moment paired with an `itemIndex` from another.
 *
 * `itemCount`/`playlistName` cover Content and Signage (Ad is confirmed
 * unreachable for Foyer, see `ScheduleClient.getAdSlot`'s own doc comment).
 */
function selectTelemetry(
  snapshot: Snapshot
): Omit<SignagePlaybackReport, "state" | "revision"> {
  const playbackType = selectPlaybackType(snapshot);
  const currentAsset = selectCurrentAsset(snapshot);
  const breakActive = selectIsSignagePlaying(snapshot);

  if (playbackType === ScheduleType.Content) {
    const content = selectCurrentContent(snapshot);
    return {
      playlistName: content?.name,
      itemIndex: currentAsset
        ? selectCurrentAssetIndex(snapshot, ScheduleType.Content) + 1
        : undefined,
      itemCount: content?.items.length,
      breakActive,
    };
  }
  if (playbackType === ScheduleType.Signage) {
    return {
      itemIndex: currentAsset
        ? selectCurrentAssetIndex(snapshot, ScheduleType.Signage) + 1
        : undefined,
      itemCount:
        snapshot.context.playback.signage.currentSignagePlaylist.length,
      breakActive,
    };
  }
  return { breakActive };
}

/**
 * The ported-engine alternative to `SignageSurface.tsx`, feeding
 * `nenda-autoplayer`'s battle-tested XState playback engine from Foyer's own
 * resolved timeline in-process (no second network hop - see
 * `FoyerScheduleClient`'s own doc comment).
 *
 * ## Scope of THIS version - read before wiring this in anywhere real
 *
 * This is the CORE wiring plus pre-roll plus clock-skew correction plus a
 * diagnostics overlay plus a three-way router: fetch the timeline, feed the
 * ported machine, render what it decides through `DualLayerPlayback` (§6b of
 * the port plan - preserving pre-roll was Sigge's explicit, binding call)
 * UNLESS Foyer's own resolver (`playheadAt`, the same pure function
 * `SignageSurface.tsx` uses) says the item actually playing right now is
 * something the ported engine cannot handle at all:
 *
 *  - a `template`/`url` kind it was never told exists
 *    (`legacyScheduleFromTimeline`'s `playableOnLegacy` drops both before
 *    they reach the wire schedule) -> `NativeFallbackLayer`.
 *  - a packaged-FILE video on a panel with native AVPlay
 *    (`avplayAvailable()`), per the SAME "provenance, not the file
 *    extension" rule `SignageSurface.tsx` enforces (`isAbrItem`, reused
 *    unchanged) -> `AvPlayFallbackLayer`, which - like `NativeFallbackLayer`
 *    - bypasses the ported engine's own state entirely rather than trying to
 *    feed it advancement events AVPlay has no equivalent of (see that file's
 *    own header comment).
 *
 * When the ported engine's own path is the one that applies, PLAYER-6/
 * Verimatrix DRM is resolved for it too: `findSignageItemById` (see
 * `playerSixDrm.ts`) maps the ported machine's `currentAsset.id` back to
 * Foyer's own resolved `SignageItem` (the wire schedule carries no
 * `contentAssetId` at all, but the two share the SAME id space - confirmed
 * by direct read of `legacySignageSchedule.ts`, no schema change needed),
 * `resolveItemPlayback` (unchanged from `SignageSurface.tsx`, its own
 * `playbackLicence.ts`) mints a licence, and the SAME rights rule applies:
 * a protected item with no successful licence renders black, never a clear
 * fallback - never `DualLayerPlayback` with no DRM at all.
 *
 * The same `findSignageItemById` join also feeds the on-device asset cache
 * (`localAssetCache.ts`, unchanged - the SAME module `SignageSurface.tsx`
 * itself uses, keyed by the item's own relative `assetUrl`, which is why the
 * join is needed at all: the wire schedule's `MediaAsset.locator` is
 * already-ABSOLUTISED and would never hit the cache's key space directly).
 * Primed on every timeline change (init/evict/queue, identical shape to
 * `SignageSurface.tsx`'s own effect) and consulted for BOTH the active and
 * the pre-rolling asset - a cache hit takes priority over the timeline's own
 * URL exactly the way a DRM-resolved URL does, and the two compose in the
 * same order `SignageSurface.tsx`'s own `playbackSrc` uses: licence wins,
 * then cache, then the plain remote URL.
 *
 * Also reports real telemetry from the machine's own selectors, corrects the
 * ported engine's wall-clock-key lookups for device clock drift
 * (`clockSkew.ts`) the same way `SignageSurface.tsx`'s own `skewMs` does - a
 * TV's RTC has "no NTP guarantee", that file's own words - and, when
 * `diagnostics` is true (CMS preview only, never in a room), renders
 * `SignageDiagnostics` - a fresh, smaller implementation covering the same
 * job as `SignageSurface.tsx`'s own `Diagnostics`, not a port of it (that
 * component is deeply coupled to internals this surface doesn't have).
 *
 * This closes every gap this surface's own header comment has ever listed.
 * What remains is what the port's own plan file has always scoped as
 * separate work: on-hardware verification of the AvPlay routing above (built
 * and unit-tested, never run against real firmware this session), and
 * actually flipping `PremiseScreen.signageEngine` to `"ported"` anywhere -
 * gated on Phase 0 (both hasselbacken screens stable), which remains unmet.
 * This surface exists to be extended incrementally, verified at each step
 * the same way the rest of the port has been - not to be swapped in for
 * `SignageSurface.tsx` in its current state.
 */

export interface SignageEngineSurfaceProps {
  screenId: string;
  source: SignageSource;
  apiBase?: string;
  at?: string;
  /** Explain an empty/blocked screen instead of just being black. Preview
   *  only - matches `SignageSurface.tsx`'s own prop of the same name. */
  diagnostics?: boolean;
  /** Where an effect channel's logo sits. Absent -> the shared defaults -
   *  matches `SignageSurface.tsx`'s own prop of the same name exactly, so
   *  `App.tsx`'s `Launcher` needs only a one-line component swap. */
  logoPlacement?: ChannelLogoPlacement;
}

/** Matches `SignageSurface.tsx`'s own poll cadence - see that file's
 *  `POLL_MS` doc comment. Two renderers polling the same endpoint on the
 *  same cadence is deliberate parity, not coincidence. */
const POLL_MS = 30_000;
/** Matches `SignageSurface.tsx`'s own `HORIZON_HOURS`. */
const HORIZON_HOURS = 6;

export default function SignageEngineSurface({
  screenId,
  source,
  apiBase = "",
  at,
  diagnostics = false,
  logoPlacement,
}: SignageEngineSurfaceProps) {
  const [timeline, setTimeline] = useState<SignageTimeline | null>(null);
  const [pollStatus, setPollStatus] = useState<PollStatus>({
    state: "loading",
  });
  const [skewSeconds, setSkewSeconds] = useState(0);
  // Defined early (not just above the router below, where it used to live)
  // because the dead-item tracking further down also needs a skew-aware
  // "now" for marking/expiring entries, same as `clockNow` already gave the
  // router.
  const clockNow = () => new Date(Date.now() + skewSeconds * 1000);
  // Decided ONCE, at mount - same reasoning as SignageSurface.tsx's own
  // `nativePlayer` state: `webapis.avplay` neither appears nor disappears
  // during a session, and re-reading it per render would make the choice of
  // player something that can change underneath a clip already playing.
  const [nativePlayer] = useState(() => avplayAvailable());
  // 1Hz re-render, same cadence as SignageSurface.tsx's own tick - the only
  // reason this surface needs one at all is the router below (template/url,
  // AVPlay): `playheadAt` is a pure function of the clock, and its answer
  // must be re-evaluated as the wall clock moves even though nothing else
  // here changed (the ported machine's own 500ms tick drives everything
  // else).
  const [, setClockTick] = useState(0);
  // Kept-alive web frames (`webFrameHost.ts`) live outside React, so nothing
  // else removes them when the wall itself goes away.
  useEffect(() => () => clearWebFrames(), []);

  useEffect(() => {
    const id = window.setInterval(() => setClockTick((n) => n + 1), 1000);
    return () => window.clearInterval(id);
  }, []);
  // The machine's own `fetchSchedule` actor calls
  // `FoyerScheduleClient.getCurrentTimeline` from inside its OWN async
  // lifecycle, decoupled from React's render cycle - a plain closure over
  // `timeline` state would read whatever was true when the client was
  // constructed, not what is true NOW. Written synchronously in `pull()`
  // itself (not via a `useEffect` mirroring `timeline` state) - see the
  // comment on `pull()` for why that distinction is load-bearing, not
  // stylistic.
  const timelineRef = useRef<SignageTimeline | null>(null);

  const token = source.kind === "screen" ? source.token : null;
  const premiseId = source.kind === "operator" ? source.premiseId : null;

  // Gates starting the actor until the FIRST poll attempt has settled
  // (success OR failure) - see `pull()`'s own comment on why racing the
  // actor's start against an in-flight first fetch is a real, not
  // theoretical, startup-latency bug.
  const [firstPollSettled, setFirstPollSettled] = useState(false);

  /* -- poll: same shape as SignageSurface.tsx's own, including the
   * clock-skew correction (see the setClockSkewMs call below) ---------- */
  useEffect(() => {
    let alive = true;
    const url = token
      ? `${apiBase}/api/foyer/screens/${encodeURIComponent(
          screenId
        )}/signage?hours=${HORIZON_HOURS}`
      : `${apiBase}/api/foyer/premise/${encodeURIComponent(
          premiseId ?? ""
        )}/signage/screens/${encodeURIComponent(
          screenId
        )}/timeline?hours=${HORIZON_HOURS}${
          at ? `&at=${encodeURIComponent(at)}` : ""
        }`;
    const pull = async () => {
      try {
        const res = await fetch(url, {
          headers: token ? { "X-Screen-Token": token } : {},
          credentials: "include",
        });
        if (!alive) return;
        if (!res.ok) {
          setPollStatus({ state: "http", code: res.status, at: Date.now() });
          return;
        }
        const next = (await res.json()) as SignageTimeline;
        if (!alive) return;
        setPollStatus({ state: "ok", at: Date.now() });
        // Written directly to the ref, not only through `setTimeline` (whose
        // effect-mirrored ref update this deliberately replaces): the
        // machine's OWN `fetchSchedule` actor backs off 30s->5min on a
        // failure (`scheduleFetchDelay`), so if its FIRST invocation raced
        // ahead of a React state->effect round trip and saw a still-null
        // ref, a cold-started screen could sit blank for up to 30 EXTRA
        // seconds on top of the real fetch latency - reproduced directly by
        // this component's own tests before this fix. A synchronous ref
        // write closes that race for good, independent of React's own
        // scheduling.
        timelineRef.current = next;
        setTimeline((cur) => (cur?.revision === next.revision ? cur : next));
        // Re-measured on EVERY poll, including ones that change nothing -
        // drift is continuous, and this closes the device-clock-skew gap
        // SignageEngineSurface's own header comment used to flag as open:
        // the ported machine's wall-clock-key lookups (clockSkew.ts) now
        // read this correction instead of the device's raw, possibly
        // drifting clock. Same computation, same sign convention, as
        // SignageSurface.tsx's own `skewMs` ref.
        const generatedAt = Date.parse(next.generatedAt);
        if (Number.isFinite(generatedAt)) {
          const skew = generatedAt - Date.now();
          setClockSkewMs(skew);
          // `setClockSkewMs` alone doesn't re-render anything (it's a plain
          // module-level value, deliberately - see clockSkew.ts's own doc
          // comment) - this piece of React state exists only so the
          // diagnostics overlay can display it.
          setSkewSeconds(Math.round(skew / 1000));
        }
      } catch {
        // Offline: the last good timeline stays live, same as
        // SignageSurface.tsx - the machine's own fetchSchedule actor also
        // retries with backoff on its own cadence regardless.
        if (alive) setPollStatus({ state: "offline", at: Date.now() });
      } finally {
        if (alive) setFirstPollSettled(true);
      }
    };
    void pull();
    const id = window.setInterval(pull, POLL_MS);
    return () => {
      alive = false;
      window.clearInterval(id);
    };
  }, [screenId, token, premiseId, apiBase, at]);

  /* -- the ported engine -------------------------------------------------- */

  // Constructed once per mount, not per render: a fresh ScheduleClient/actor
  // on every render would tear down and restart the machine's own
  // retry/backoff state constantly.
  const clients = useMemo(
    () => ({
      scheduleClient: new FoyerScheduleClient(() => timelineRef.current),
      logClient: new WebLogClient(),
      deviceController: WebDeviceController.getInstance(),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const actorRef = useMemo(
    () =>
      createActor(
        autoplayerMachine(
          clients.scheduleClient,
          clients.logClient,
          clients.deviceController
        ),
        {
          input: {
            session: {
              screenId,
              premiseId: premiseId ?? "",
              password: "",
            },
          },
        }
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [clients]
  );

  useEffect(() => {
    if (!firstPollSettled) return;
    actorRef.start();
    return () => {
      actorRef.stop();
    };
  }, [actorRef, firstPollSettled]);

  /* -- telemetry: real machine state, not wall-clock estimation ----------- */

  const currentAsset = useSelector(actorRef, selectCurrentAsset);
  const isCircuitBreakerOpen = useSelector(
    actorRef,
    selectIsPlaybackCircuitBreakerOpen
  );
  const isOffline = useSelector(actorRef, (s) => s.matches("Offline"));
  const telemetry = useSelector(actorRef, selectTelemetry);

  const state: SignagePlaybackReport["state"] = isCircuitBreakerOpen
    ? "blocked"
    : isOffline
      ? "blocked"
      : currentAsset
        ? "playing"
        : "idle";

  // Distinguishes the three real causes of "nothing on screen" this surface
  // can actually tell apart today - matches the spirit of
  // SignageSurface.tsx's own `explainEmpty`, which does the same job from a
  // wall-clock estimate rather than a real state-machine snapshot.
  const idleReason = isCircuitBreakerOpen
    ? "Playback circuit breaker is open after repeated failures - the engine is backing off before retrying."
    : isOffline
      ? "The engine's own machine is in its Offline state."
      : !currentAsset
        ? "No current asset yet - the schedule hasn't resolved, or nothing is scheduled for this instant."
        : undefined;

  const report: SignagePlaybackReport = {
    state,
    revision: timeline?.revision,
    idleReason,
    ...telemetry,
  };

  useEffect(() => {
    reportSignagePlayback(report);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, idleReason, telemetry, timeline?.revision]);

  /* -- PLAYER-6/Verimatrix DRM for the ported engine's own active asset --- */

  // `findSignageItemById`, not `head.item` (below): the ported machine's
  // `currentAsset` can be mid-campaign-break, which `playheadAt`/`head`
  // never resolves at all (it only walks `timeline.content`/`.playlists`) -
  // see playerSixDrm.ts's own doc comment.
  const contentAssetId =
    currentAsset && timeline
      ? findSignageItemById(timeline, currentAsset.id)?.contentAssetId
      : undefined;
  const [licensed, setLicensed] = useState<ResolvedItemPlayback | null>(null);
  useEffect(() => {
    if (!contentAssetId) {
      setLicensed(null);
      return;
    }
    const caller: PlaybackCaller | null = token
      ? { kind: "screen", screenId, token }
      : premiseId
        ? { kind: "operator", premiseId }
        : null;
    if (!caller) {
      setLicensed(null);
      return;
    }
    let alive = true;
    void resolveItemPlayback({ apiBase, caller, contentAssetId }).then((r) => {
      if (alive) setLicensed(r.playback);
    });
    return () => {
      alive = false;
    };
  }, [contentAssetId, token, premiseId, screenId, apiBase]);
  // "Black, briefly, rather than a flash of the wrong thing" -
  // SignageSurface.tsx's own rule, unchanged: a refusal (or the brief
  // resolving window before the first response) must never fall through to
  // playing the clear rendition. Only meaningful for the ported engine's own
  // path below - NativeFallbackLayer/AvPlayFallbackLayer items are never
  // resolved against `currentAsset.id` in the first place (see the
  // paragraph above).
  const blockedByLicence = Boolean(contentAssetId) && !licensed;
  // ONE object per resolved licence, not one per render. This surface
  // re-renders every second (the clock tick) and `toDrmConfig` returns a new
  // object literal, so building it inline in the JSX handed ShakaPlayer a
  // "new" `drm` every second; its main effect depends on `drm` and ends in
  // `player.load()`, so a protected item (a live channel) was reconfigured and
  // reloaded about twice a second for as long as it played (2026-09-28/29:
  // ~1.5 Widevine licence requests per second, and the stall and startup
  // watchdogs, which are timers inside that effect, could never fire).
  // Upstream nenda-autoplayer keeps `drm` in useState, set once per resolve.
  const activeDrm = useMemo(() => toDrmConfig(licensed), [licensed]);

  /* -- on-device asset cache: identical shape to SignageSurface.tsx's own - */

  const revision = timeline?.revision;
  useEffect(() => {
    if (!timeline) return;
    initSignageCache();
    const urls = Array.from(
      new Set(
        timeline.playlists
          .flatMap((p) => p.items)
          .concat(timeline.breaks.flatMap((b) => b.items))
          .map((i) => i.assetUrl)
          .concat(
            (timeline.templates ?? []).flatMap((t) =>
              Object.values(t.values ?? {}).filter((v) =>
                v.startsWith("/foyer-assets/")
              )
            )
          )
          .filter((u) => !!u && !/\.(mpd|m3u8)(\?|$)/i.test(u))
      )
    );
    evictStale(urls);
    queueSignageAssets(urls);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeline, revision]);

  const nextAsset = useSelector(actorRef, selectNextAsset);
  /**
   * A cached copy for either layer's asset, or `undefined` to leave the
   * timeline's own URL alone - never a manifest (`isManifestUrl`, same
   * guard `SignageSurface.tsx`'s own `playbackCacheHit` uses: a manifest is
   * a playlist of segments, caching the `.mpd` itself buys nothing).
   */
  const cachedSrcFor = (assetId: string | undefined): string | undefined => {
    if (!assetId || !timeline) return undefined;
    const found = findSignageItemById(timeline, assetId);
    if (!found || isManifestUrl(found.assetUrl)) return undefined;
    return getLocalUri(found.assetUrl) ?? undefined;
  };
  // Same priority order as SignageSurface.tsx's own `playbackSrc`: a
  // licence-resolved rendition wins, THEN a cached copy, THEN the plain
  // remote URL (DualLayerPlayback's own default when neither override is
  // set).
  const activeSrcOverride = licensed?.url ?? cachedSrcFor(currentAsset?.id);
  const preRollSrcOverride = cachedSrcFor(nextAsset?.id);

  /* -- dead-item tracking for the two router branches that bypass the ported
   * machine's own retry/circuit-breaker handling entirely -------------------
   *
   * `NativeFallbackLayer` (template/url) and `AvPlayFallbackLayer` (native
   * AVPlay) are both driven straight off `playheadAt`, never off the ported
   * machine's state - so unlike the `DualLayerPlayback` path (which inherits
   * autoplayer's own `ASSET_COMPLETE`+circuit-breaker handling verbatim),
   * neither had any way to remember "this specific item just failed" until
   * now. Exact same shape as `SignageSurface.tsx`'s own `dead`/`markDead`/
   * `currentlyDead` (CONTENT-29's fix - the literal bug this whole port
   * exists to fix, "Samsung signage stuck on item 1/75") - reused via the
   * exported `pickPlayable`/`pickPlayableIndex`/`DEAD_RETRY_MS`, not
   * re-derived. A `Map<id, deadAtMs>` rather than a plain `Set` so an entry
   * expires after `DEAD_RETRY_MS` instead of exiling an item for the rest of
   * the panel's uptime over one transient failure.
   */
  const dead = useRef<Map<string, number>>(new Map());
  const [, setDeadVersion] = useState(0);
  const markDead = (id: string) => {
    dead.current.set(id, clockNow().getTime());
    setDeadVersion((v) => v + 1);
  };
  const nowMs = clockNow().getTime();
  const currentlyDead = new Set<string>();
  for (const [id, deadAtMs] of dead.current) {
    if (nowMs - deadAtMs < DEAD_RETRY_MS) currentlyDead.add(id);
  }

  /* -- router: the ported engine, or one of Foyer's own native fallbacks -- */

  const head = timeline ? playheadAt(timeline, clockNow()) : null;
  // Dead-aware pick, not `head.item` raw: an item AVPlay just reported dead
  // must not be routed right back to AVPlay (or anywhere else) on the very
  // next tick - walk forward to whatever's actually live, exactly as
  // `SignageSurface.tsx`'s own `item = pickPlayable(...)` does. The result
  // can be ANY kind, not just the raw item's own - a dead video can hand the
  // wall to a template/url item or back to the ported engine, same as today.
  const effectiveItem = head
    ? pickPlayable(head.playlist.items, head.index, currentlyDead)
    : null;
  const effectiveIndex = head
    ? pickPlayableIndex(head.playlist.items, head.index, currentlyDead)
    : null;
  const nativeItem =
    effectiveItem &&
    (effectiveItem.kind === "template" || effectiveItem.kind === "url")
      ? effectiveItem
      : null;
  // "Provenance, not the file extension" - `isAbrItem` reused unchanged from
  // SignageSurface.tsx (see AvPlayFallbackLayer.tsx's own header comment).
  // Never true for a manifest item regardless of platform: AVPlay is for a
  // packaged FILE, confirmed by direct hardware testing in the current
  // renderer (a `<video src>` on these Samsung panels fetches every byte and
  // then declines to decode it - see avplay.ts).
  const avPlayHead =
    nativePlayer &&
    head &&
    effectiveItem &&
    effectiveIndex !== null &&
    effectiveItem.kind === "video" &&
    !isAbrItem(effectiveItem, effectiveItem.assetUrl)
      ? { ...head, item: effectiveItem, index: effectiveIndex }
      : null;

  // Only a curated EFFECT channel gets a logo, and only once it has one set
  // - matches SignageSurface.tsx's own rule exactly (same `head`-shaped
  // resolver result, same fields). Rendered over WHICHEVER of the three
  // router branches is active: an effect channel is a `video`/`image`
  // content loop by definition (never template/url), but it CAN legitimately
  // be a packaged file routed through AvPlayFallbackLayer, so this sits
  // outside the router's own if/else rather than inside one branch of it.
  const resolvedLogoPlacement: ChannelLogoPlacement = logoPlacement ?? {
    x: DEFAULT_CHANNEL_LOGO_OFFSET_X,
    y: DEFAULT_CHANNEL_LOGO_OFFSET_Y,
    width: DEFAULT_CHANNEL_LOGO_WIDTH,
  };
  const channelLogo =
    head?.playlist.effect && head.playlist.logoUrl ? (
      <ChannelLogoOverlay
        logoUrl={head.playlist.logoUrl}
        placement={resolvedLogoPlacement}
      />
    ) : null;
  // CONTENT-26: whoever supplied the item playing RIGHT NOW, bottom-right -
  // opposite the channel's own mark, because on an effect channel carrying a
  // catalogue title both are true at once. Effect channels only (ruled
  // 2026-09-27), and only when the asset actually carries a `logo` image:
  // `supplierLogoUrl` is absent for a live channel by construction, so this
  // never double-draws the channel mark. The corner comes from the authority,
  // not from here.
  const supplierLogo =
    head?.playlist.effect && effectiveItem?.supplierLogoUrl ? (
      <ChannelLogoOverlay
        claimant="supplier-logo"
        testId="supplier-logo-overlay"
        logoUrl={effectiveItem.supplierLogoUrl}
        placement={resolvedLogoPlacement}
      />
    ) : null;

  return (
    <div
      style={{ position: "fixed", inset: 0, background: "#000" }}
      data-testid="signage-engine-surface"
      data-revision={timeline?.revision}
    >
      {nativeItem && timeline ? (
        <NativeFallbackLayer
          item={nativeItem}
          timeline={timeline}
          now={clockNow}
        />
      ) : avPlayHead ? (
        <AvPlayFallbackLayer
          head={avPlayHead}
          soundEnabled={timeline?.soundEnabled ?? false}
          dead={currentlyDead}
          onItemFailed={markDead}
        />
      ) : blockedByLicence ? null : (
        <DualLayerPlayback
          actorRef={actorRef}
          send={actorRef.send}
          activeDrm={activeDrm}
          activeSrcOverride={activeSrcOverride}
          preRollSrcOverride={preRollSrcOverride}
          // Pre-roll OFF (Sigge, 2026-09-29: "let's drop the pre-roll for
          // now"). A second video pipeline starting next to a cold active one
          // makes the Samsung QB24C pause the active <video>, permanently:
          // 4 of 4 cold starts with pre-roll on stalled or froze (hasselbacken/
          // 302, paused at t=0.18s with the data all buffered, the second
          // player created 88-129 ms after the first frame), and with it off
          // they played. The cold-start gate (1d25ec9) could not fix it: it
          // only made the pause permanent, because `playing` also clears the
          // 30 s startup watchdog that used to fail the item and move on.
          // This is the upstream nenda-autoplayer model (one item, one
          // player), which also never ran two decoders on this panel.
          //
          // NOT a verdict on pre-roll for every panel, and the known cost is
          // the teardown TypeError seen during the 2026-09-25 weekend soak.
          // The real question, whether AVPlay can play the avc1 effect
          // manifests so this cannot happen at all, is open.
          disablePreRoll
        />
      )}
      {channelLogo}
      {supplierLogo}
      {diagnostics && (
        <SignageDiagnostics
          screenId={screenId}
          timeline={timeline}
          pollStatus={pollStatus}
          report={report}
          clockSkewSeconds={skewSeconds}
        />
      )}
    </div>
  );
}
